// PRD.314 — generic encounter-scoped Specialty History.
//
// ONE relation (`encounter_specialty_histories`) serves every specialty, keyed
// by a stable clinical `type`. Medical History is NOT here — it remains
// `patient_encounters.medical_history` (PRD.310).
//
// This module is PURE and self-contained (no workspace-package imports) so it
// runs under the node:test harness. The PowerSync QuerySpecs live next door in
// `specialty-histories.ts`, which re-exports everything here.

// ─── Types ───

/**
 * Stable stored clinical identity. NEVER the UI label — a label rename must
 * never rewrite clinical data (PRD §6.3).
 */
export type SpecialtyHistoryType = 'dental' | 'derma';

export const SPECIALTY_HISTORY_TYPES: readonly SpecialtyHistoryType[] = ['dental', 'derma'];

/** Presentation labels for each stored type. */
export const SPECIALTY_HISTORY_LABELS: Record<SpecialtyHistoryType, string> = {
  dental: 'Dental History',
  derma: 'Derma History',
};

/**
 * Visit Note sub-module key ↔ stored clinical type. The UI key is flat and
 * lives alongside the other catalogue keys; the stored identity stays
 * `dental` / `derma`. A UI key must never become a clinical column (PRD §13).
 */
export const SPECIALTY_HISTORY_KEY_BY_TYPE: Record<SpecialtyHistoryType, string> = {
  dental: 'dental_history',
  derma: 'derma_history',
};

export interface EncounterSpecialtyHistoryRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  encounter_id: string | null;
  type: string | null;
  text: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

// ─── Identity ───

/**
 * Deterministic row id for one encounter + specialty type.
 *
 * Follows the `userUiPreferenceId` precedent. Two devices editing the same
 * encounter+type derive the SAME id, so SyncWrite's `ON CONFLICT (id) DO
 * UPDATE` converges them instead of both inserting and one violating
 * `UNIQUE(encounter_id, type)` — which would wedge the upload queue.
 */
export function specialtyHistoryId(encounterId: string, type: SpecialtyHistoryType): string {
  return `${encounterId}:${type}`;
}

// ─── Selection ───

/** One prior encounter's specialty narrative, as the surface renders it. */
export interface PriorSpecialtyHistoryEntry {
  encounterId: string;
  /** Best available visit date ('YYYY-MM-DD' or ISO). Null when unresolvable. */
  visitDate: string | null;
  /** Never empty — blanks are filtered out. */
  text: string;
}

/**
 * Build the read-only prior-entries list for one specialty type.
 *
 * Same rules as Medical History (PRD.313 §5.2):
 *  - the ACTIVE encounter is excluded — today's entry is the editor;
 *  - encounters AFTER the active one are excluded, so reopening an older visit
 *    never shows later visits as its history;
 *  - blank narratives are dropped;
 *  - oldest first, so history runs forwards into the current entry.
 *
 * `chronologyByEncounter` carries each encounter's own timestamp (used for
 * ordering and the before/after test, since it distinguishes same-day visits),
 * while `visitDateByEncounter` supplies the DISPLAY date from the app's
 * canonical resolver. Pure: selects rows for display, never writes.
 */
export function selectPriorSpecialtyHistory(
  rows: readonly EncounterSpecialtyHistoryRow[],
  activeEncounterId: string,
  visitDateByEncounter: ReadonlyMap<string, string | null>,
  chronologyByEncounter: ReadonlyMap<string, string | null>,
): PriorSpecialtyHistoryEntry[] {
  const activeKey = chronologyByEncounter.get(activeEncounterId) ?? null;

  const entries: { key: string | null; entry: PriorSpecialtyHistoryEntry }[] = [];
  for (const row of rows) {
    const encounterId = row.encounter_id;
    if (!encounterId || encounterId === activeEncounterId) continue;
    const text = (row.text ?? '').trim();
    if (!text) continue;
    const key = chronologyByEncounter.get(encounterId) ?? null;
    if (activeKey) {
      if (!key || key >= activeKey) continue;
    }
    entries.push({
      key,
      entry: { encounterId, visitDate: visitDateByEncounter.get(encounterId) ?? null, text },
    });
  }

  entries.sort((a, b) => {
    if (!a.key && !b.key) return 0;
    if (!a.key) return 1;
    if (!b.key) return -1;
    return a.key.localeCompare(b.key);
  });
  return entries.map((e) => e.entry);
}

/** The active encounter's current narrative for one type, or '' when absent. */
export function selectCurrentSpecialtyHistory(
  rows: readonly EncounterSpecialtyHistoryRow[],
  activeEncounterId: string,
): string {
  const row = rows.find((r) => r.encounter_id === activeEncounterId);
  return row?.text ?? '';
}
